import { Button } from '@konova/ui/button';
import { FormikFieldWrapper } from '@konova/ui/formik-field-wrapper';
import { Icon } from '@konova/ui/icon';
import { InfoBox } from '@konova/ui/info-box';
import { Modal } from '@konova/ui/modal';
import { Stack } from '@konova/ui/stack';
import { TextInput } from '@konova/ui/text-input';
import { Typography } from '@konova/ui/typography';
import { useToast } from '@konova/ui/use-toast';
import { IResponse } from '@legacy/types';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { UserService } from '../../services/UserService.ts';

const formSchema = () =>
  yup.object().shape({
    token: yup.string().required(
      i18next.t('components.validation-message.field-required', {
        fieldName: i18next.t('pages.2fa.confirmation-code-label'),
        suffix: i18next.t('common.suffix.objective')
      })
    ).min(6, i18next.t('components.validation-message.min-varchar-length', {
      minVarcharLength: 6
    }))
  });

interface IProps {
  number: string;
  onClose: () => void;
  handleRequest: (requestPromise: Promise<undefined>, suppressGlobalErrorToast?: boolean) => Promise<IResponse>;
  afterSubmit: () => Promise<void>;
}

const ConfirmationModal = ({ number, onClose, handleRequest, afterSubmit }: IProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const [counter, setCounter] = useState(0);
  const [isResending, toggleIsResending] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (counter > 0) {
      setTimeout(() => setCounter(counter - 1), 1000);
    }
  }, [counter]);

  const sendCode = async () => {
    setCounter(30);
    await handleRequest(UserService.verifySetup2FA({ phone: number }));
    toggleIsResending(false);
  };

  const handleSubmit = (values: {token: string}, formikHelpers: FormikHelpers<{token: string}>) => {
    setLoading(true);
    handleRequest(UserService.verifyPhone2FA(values), true)
      .then(() => {
        onClose();
        toast.add(t<string>('components.account.change-phone-number-modal.change-success-message'), 'success');
        return afterSubmit();
      })
      .catch(err => {
        if (err.statusCode === 429) {
          formikHelpers.setErrors({ token: t<string>('common.too-many-request') });
        } else if (err.statusCode === 422) {
          formikHelpers.setErrors({
            token: t<string>('pages.2fa.entered-confirmation-code-wrong-validation')
          });
        } else {
          formikHelpers.setErrors({
            token: t<string>(err.body.errors?.token[0] || err.body.message)
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Formik<{token: string}>
      initialValues={{
        token: ''
      }}
      onSubmit={handleSubmit}
      enableReinitialize
      validationSchema={formSchema()}
    >
      {({ submitForm }) => (
        <Modal
          open
          onClose={onClose}
          title={t<string>('components.account.change-phone-number-modal.title')}
          label={t<string>('components.account.change-phone-number-modal.title')}
          className="confirmation-modal"
          footer={(
            <>
              <Button variant="text" onClick={onClose} disabled={isLoading}>
                {t<string>('common.cancel')}
              </Button>

              <Button
                onClick={submitForm}
                type="submit"
                prefix={<Icon icon={['far', 'check']}/>}
                loading={isLoading}
              >
                {t('common.save')}
              </Button>
            </>
          )}
        >
          <Form noValidate>
            <Stack direction="column" spacing={3} crossAxis="stretch">
              <InfoBox color={'info'}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: t<string>('components.account.change-phone-number-modal.information',
                      {
                        phone_number: number
                      }
                    )
                  }}
                />
              </InfoBox>

              <Stack direction="column" crossAxis="stretch">
                <Field name="token">
                  {({ form, field }) => (
                    <FormikFieldWrapper field={field.name}>
                      <TextInput
                        label={t('pages.2fa.confirmation-code-label')}
                        placeholder={t<string>('pages.2fa.confirmation-code-placeholder')}
                        required
                        value={field.value}
                        autoFocus
                        onChange={event => form.setFieldValue(field.name, event.target.value)}
                      />
                    </FormikFieldWrapper>
                  )}
                </Field>

                <Typography>
                  {t<string>('pages.2fa.no-confirmation-code-received')}&nbsp;
                  <Button
                    inline
                    variant={'link'}
                    disabled={counter > 0 || isResending}
                    typoProps={{
                      bold: true
                    }}
                    onClick={async () => {
                      toggleIsResending(true);
                      await sendCode();
                    }}
                  >
                    {t<string>('pages.login.user-blocked-form.send-again')}
                    {counter !== 0 && ` (${counter})`}
                  </Button>
                </Typography>
              </Stack>
            </Stack>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

export default ConfirmationModal;
